import { ReactNode, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Button } from "components/button";
import { Loader } from "components/loading/Loader";
import { PickerProject } from "./AddToProjectPicker.types";
import { usePickerState } from "./usePickerState";
import { ProjectListSection } from "./ProjectListSection";
import { ProjectDrilldownList } from "./ProjectDrilldownList";
import { NoProjectsAlert } from "./NoProjectsAlert";

interface AddToProjectPickerContentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  header: ReactNode;
  myProjects: PickerProject[];
  sharedProjects: PickerProject[];
  onAssign: (project: PickerProject) => void;
  onAlreadyAssigned?: (project: PickerProject) => void;
  onCreateNew: () => void;
  isAssigning?: boolean;
  isLoading?: boolean;
}

export const AddToProjectPickerContent = ({
  open,
  onOpenChange,
  header,
  myProjects,
  sharedProjects,
  onAssign,
  onAlreadyAssigned,
  onCreateNew,
  isAssigning,
  isLoading,
}: AddToProjectPickerContentProps) => {
  const { t } = useTranslation(["components"]);
  const {
    mode,
    query,
    setQuery,
    openDrilldown,
    closeDrilldown,
    reset,
    overview,
    search,
    drilldown,
  } = usePickerState({ myProjects, sharedProjects });
  const assignedSelectionEnabled = Boolean(onAlreadyAssigned);

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const handleSelect = (project: PickerProject) => {
    if (isAssigning) {
      return;
    }
    if (project.assigned) {
      onAlreadyAssigned?.(project);
      return;
    }
    onAssign(project);
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <div
          className="flex justify-center py-10"
          data-testid="add-to-project-picker-loading"
        >
          <Loader className="h-8 w-8 border-4" />
        </div>
      );
    }

    if (mode === "drilldown" && drilldown) {
      return (
        <ProjectDrilldownList
          title={
            drilldown.scope === "my"
              ? t("components:addToProjectPicker.drilldown.myTitle")
              : t("components:addToProjectPicker.drilldown.sharedTitle")
          }
          backLabel={t("components:addToProjectPicker.backLabel")}
          count={drilldown.count}
          groups={drilldown.groups}
          selectionDisabled={isAssigning}
          assignedSelectionEnabled={assignedSelectionEnabled}
          onBack={closeDrilldown}
          onSelect={handleSelect}
        />
      );
    }

    if (mode === "search") {
      if (search.isEmpty) {
        return (
          <NoProjectsAlert
            title={t("components:addToProjectPicker.noResults")}
          />
        );
      }
      return (
        <>
          <ProjectListSection
            label={t("components:addToProjectPicker.sections.myProjects")}
            projects={search.my}
            highlightQuery={query}
            selectionDisabled={isAssigning}
            assignedSelectionEnabled={assignedSelectionEnabled}
            onSelect={handleSelect}
          />
          <ProjectListSection
            label={t("components:addToProjectPicker.sections.sharedProjects")}
            projects={search.shared}
            highlightQuery={query}
            selectionDisabled={isAssigning}
            assignedSelectionEnabled={assignedSelectionEnabled}
            onSelect={handleSelect}
          />
        </>
      );
    }

    if (overview.myCount === 0 && overview.sharedCount === 0) {
      return (
        <NoProjectsAlert
          title={t("components:addToProjectPicker.noProjects")}
        />
      );
    }

    return (
      <>
        <ProjectListSection
          label={t("components:addToProjectPicker.sections.recent")}
          projects={overview.recent}
          leadingIcon="history"
          selectionDisabled={isAssigning}
          assignedSelectionEnabled={assignedSelectionEnabled}
          onSelect={handleSelect}
        />
        <ProjectListSection
          label={t("components:addToProjectPicker.sections.myProjects")}
          projects={overview.myTop}
          selectionDisabled={isAssigning}
          assignedSelectionEnabled={assignedSelectionEnabled}
          onSelect={handleSelect}
          seeAllLabel={
            overview.hasMoreMy
              ? t("components:addToProjectPicker.seeAllMy", {
                  total: overview.myCount,
                })
              : undefined
          }
          onSeeAll={overview.hasMoreMy ? () => openDrilldown("my") : undefined}
        />
        <ProjectListSection
          label={t("components:addToProjectPicker.sections.sharedProjects")}
          projects={overview.sharedTop}
          selectionDisabled={isAssigning}
          assignedSelectionEnabled={assignedSelectionEnabled}
          onSelect={handleSelect}
          seeAllLabel={
            overview.hasMoreShared
              ? t("components:addToProjectPicker.seeAllShared", {
                  total: overview.sharedCount,
                })
              : undefined
          }
          onSeeAll={
            overview.hasMoreShared ? () => openDrilldown("shared") : undefined
          }
        />
      </>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[70vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-xl"
      >
        <DialogHeader className="flex-shrink-0 space-y-0 px-5 pt-5 text-left">
          <DialogTitle className="sr-only">
            {t("components:addToProjectPicker.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("components:addToProjectPicker.description")}
          </DialogDescription>
          {header}
        </DialogHeader>

        <div className="flex-shrink-0 border-b">
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("components:addToProjectPicker.searchPlaceholder")}
            aria-label={t("components:addToProjectPicker.searchLabel")}
            autoFocus
            disabled={isAssigning}
            className="w-full bg-transparent px-5 py-3 text-sm outline-0 placeholder:text-muted-foreground disabled:cursor-default disabled:opacity-60"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-2">{renderBody()}</div>

        <div className="flex-shrink-0 border-t bg-muted/30 p-2">
          <Button
            variant="ghost"
            size="medium"
            icon="folder-plus"
            iconPosition="left"
            label={t("components:addToProjectPicker.createProject")}
            className="w-full justify-center text-slate-800 hover:text-slate-800"
            disabled={isAssigning}
            onClick={onCreateNew}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
